export interface ImageConversionOptions {
  includeDataPrefix?: boolean;
  maxSize?: number;
  allowedTypes?: string[];
  corsMode?: RequestMode;
}

// File 객체를 Base64 문자열로 변환
export const convertFileToBase64 = async (
  file: File,
  options: ImageConversionOptions = {}
): Promise<string> => {
  const {
    includeDataPrefix = false,
    maxSize = 5 * 1024 * 1024,
    allowedTypes = ['image/']
  } = options;

  // 파일 크기 검증
  if (file.size > maxSize) {
    throw new Error(`파일 크기가 너무 큽니다. 최대 ${(maxSize / 1024 / 1024).toFixed(1)}MB까지 가능합니다.`);
  }

  // 파일 타입 검증
  const isValidType = allowedTypes.some(type => 
    type.endsWith('/') ? file.type.startsWith(type) : file.type === type
  );
  
  if (!isValidType) {
    throw new Error('지원되지 않는 파일 형식입니다.');
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const result = event.target?.result as string;
      if (!result) {
        reject(new Error('파일을 읽을 수 없습니다.'));
        return;
      }

      if (includeDataPrefix) {
        resolve(result);
      } else {
        const base64Data = result.split(',')[1];
        resolve(base64Data || '');
      }
    };

    reader.onerror = () => {
      reject(new Error('파일 읽기 중 오류가 발생했습니다.'));
    };

    reader.readAsDataURL(file);
  });
};

// 이미지 URL을 Base64 문자열로 변환 (Canvas API 사용 - CORS 문제 해결)
export const convertUrlToBase64 = async (
  url: string,
  options: ImageConversionOptions = {}
): Promise<string> => {
  const {
    includeDataPrefix = false,
    corsMode = 'cors'
  } = options;

  // 먼저 Canvas API를 통한 변환 시도 (CORS 우회)
  try {
    return await convertUrlToBase64WithCanvas(url, includeDataPrefix);
  } catch (canvasError) {
    console.warn('Canvas 변환 실패, fetch 방식으로 재시도:', canvasError);
    
    // Canvas 실패 시 기존 fetch 방식으로 시도
    try {
      const response = await fetch(url, {
        mode: corsMode,
        cache: 'default',
        referrer: 'no-referrer',
        headers: {
          'User-Agent': 'Mozilla/5.0 (compatible)',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = (event) => {
          const result = event.target?.result as string;
          if (!result) {
            reject(new Error('이미지를 읽을 수 없습니다.'));
            return;
          }

          if (includeDataPrefix) {
            resolve(result);
          } else {
            const base64Data = result.split(',')[1];
            resolve(base64Data || '');
          }
        };

        reader.onerror = () => {
          reject(new Error('이미지 변환 중 오류가 발생했습니다.'));
        };

        reader.readAsDataURL(blob);
      });
    } catch (fetchError) {
      console.error('URL 이미지 변환 실패:', url, fetchError);
      throw new Error(`이미지 로드 실패: ${fetchError instanceof Error ? fetchError.message : '알 수 없는 오류'}`);
    }
  }
};

// Canvas API를 사용한 이미지 변환 (CORS 문제 해결용)
const convertUrlToBase64WithCanvas = async (
  url: string, 
  includeDataPrefix: boolean
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Canvas context를 생성할 수 없습니다.'));
          return;
        }

        canvas.width = img.width;
        canvas.height = img.height;
        
        // 이미지를 canvas에 그리기
        ctx.drawImage(img, 0, 0);
        
        // canvas에서 base64 추출
        const dataURL = canvas.toDataURL('image/jpeg', 0.8);
        
        if (includeDataPrefix) {
          resolve(dataURL);
        } else {
          const base64Data = dataURL.split(',')[1];
          resolve(base64Data || '');
        }
      } catch (error) {
        reject(new Error(`Canvas 변환 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`));
      }
    };

    img.onerror = () => {
      reject(new Error('이미지 로드 실패 - CORS 또는 네트워크 문제'));
    };

    // CORS 문제 해결을 위한 crossOrigin 설정
    img.crossOrigin = 'anonymous';
    img.src = url;
  });
};

// 다양한 형태의 이미지 데이터를 API 전송용 Base64로 변환
export const prepareImageForAPI = async (
  imageData: string,
  options: ImageConversionOptions = {}
): Promise<string> => {
  if (!imageData.startsWith('http') && !imageData.startsWith('data:')) {
    return imageData;
  }

  if (imageData.startsWith('data:')) {
    const base64Data = imageData.split(',')[1];
    return base64Data || '';
  }

  if (imageData.startsWith('http')) {
    return await convertUrlToBase64(imageData, { ...options, includeDataPrefix: false });
  }

  return imageData;
};

// Base64 데이터를 이미지 표시용 data URL로 변환
export const getImageDisplayUrl = (imageData: string, mimeType: string = 'image/jpeg'): string => {
  if (imageData.startsWith('data:')) {
    return imageData;
  }

  if (imageData.startsWith('http')) {
    return imageData;
  }

  return `data:${mimeType};base64,${imageData}`;
};

// 이미지 파일 검증
export const validateImageFile = (
  file: File,
  options: ImageConversionOptions = {}
): { isValid: boolean; error?: string } => {
  const {
    maxSize = 5 * 1024 * 1024, // 5MB
    allowedTypes = ['image/']
  } = options;

  if (file.size > maxSize) {
    return {
      isValid: false,
      error: `파일 크기가 너무 큽니다. 최대 ${(maxSize / 1024 / 1024).toFixed(1)}MB까지 가능합니다.`
    };
  }

  const isValidType = allowedTypes.some(type => 
    type.endsWith('/') ? file.type.startsWith(type) : file.type === type
  );
  
  if (!isValidType) {
    return {
      isValid: false,
      error: '지원되지 않는 파일 형식입니다. 이미지 파일만 업로드 가능합니다.'
    };
  }

  return { isValid: true };
};